import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useApiClient } from '../../apiClient/useApiClient';
import { useShowError } from '../../useShowError';
import { ValueRetirement } from '../../apiClient/types';
import { useIntl } from 'react-intl';
import { ApiError } from '../../apiClient/errors';

/**
 * Layer between the apiClient and the Realms pages. Uses react-query.
 *
 * Invokes ApiClient methods. Updates the queries and mutations in the react-query cache.
 */

export const useRealms = () => {
  const queryClient = useQueryClient();
  const {
    getRealmInformation,
    editUserGroups,
    removeFromGroup,
    listRealms,
    addUsersToRealm,
    setRealmUserGroups,
  } = useApiClient();

  const showError = useShowError();
  const intl = useIntl();

  const getRealmsList = useQuery('realms', listRealms, {
    onError: (error) =>
      showError(error, {
        description: intl.formatMessage({ id: 'realms_list_realms_error' }),
      }),
  });

  const useInformationOfRealm = (realmId: string) =>
    useQuery(['realm', realmId], () => getRealmInformation(realmId), {
      onError: (error: ApiError) => {
        showError(error, {
          description: intl.formatMessage({ id: 'realms_get_information_error' }),
        });
      },
    });

  const editGroups = useMutation(
    ({
      realmGroupId,
      userId,
      retirement,
    }: {
      realmGroupId: string;
      userId: string;
      retirement?: boolean;
    }) => editUserGroups(realmGroupId, userId, retirement),
    {
      onSuccess: () => queryClient.invalidateQueries('realms'),
      onError: (error) =>
        showError(error, { description: intl.formatMessage({ id: 'realms_edit_groups_error' }) }),
    }
  );

  const removeUser = useMutation(
    ({ realmGroupId, userId }: { realmGroupId: string; userId: string }) =>
      removeFromGroup(realmGroupId, userId),
    {
      onSuccess: () => queryClient.invalidateQueries('realms'),
      onError: (error) =>
        showError(error, { description: intl.formatMessage({ id: 'realms_remove_user_error' }) }),
    }
  );

  const addUser = useMutation(
    ({
      realmId,
      userId,
      groupIds,
      retirement,
    }: {
      realmId: string;
      userId: string;
      groupIds: string[];
      retirement?: ValueRetirement;
    }) => addUsersToRealm(realmId, userId, groupIds, retirement),
    {
      onSuccess: () => queryClient.invalidateQueries('realms'),
      onError: (error) =>
        showError(error, { description: intl.formatMessage({ id: 'realms_add_user_error' }) }),
    }
  );

  const setUserGroups = useMutation(
    ({
      realmId,
      userId,
      groupIds,
      retirement,
    }: {
      realmId: string;
      userId: string;
      groupIds?: string[];
      retirement?: ValueRetirement;
    }) => setRealmUserGroups(realmId, userId, groupIds, retirement),
    {
      onSuccess: () => queryClient.invalidateQueries('realms'),
      onError: (error) =>
        showError(error, {
          description: intl.formatMessage({ id: 'realms_set_user_groups_error' }),
        }),
    }
  );

  return {
    useInformationOfRealm,
    getRealmsList,
    editGroups,
    removeUser,
    addUser,
    setUserGroups,
  };
};
